import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, CircularProgress, Box, Button } from "@mui/material";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useSelector, useDispatch } from "react-redux";
import { selectUser } from "../../../../features/userSlice";
import directClient from "../../../../api-clients/directClient";
import endpoints from "../../../../api-clients/endpoints";
import { useUserStateContext } from "App";
import { fetchAutoPilotPaymentMethodSuccess } from "features/userSlice";

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_API_KEY,
);

const PaymentForm = ({ onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { userCreds } = useUserStateContext();
  const dispatch = useDispatch();

  const getPaymentMethodDetails = async (paymentMethodId) => {
    try {
      const headers = {
        "User-Identifier": userCreds.cognitoID,
      };
      const response = await directClient.get(
        endpoints.getPaymentMethodDetails(),
        { headers, params: { payment_method_id: paymentMethodId } },
      );

      dispatch(
        fetchAutoPilotPaymentMethodSuccess({ paymentMethod: response.data }),
      );
      onPaymentSuccess();
      return response.data;
    } catch (error) {
      console.error("Error getting payment method details:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.origin + "/autopilot",
        },

        confirm: true,
      });

      if (error) {
        setMessage(error.message);
      } else if (setupIntent && setupIntent.status === "succeeded") {
        setMessage("Payment method saved successfully!");
        await getPaymentMethodDetails(
          setupIntent.payment_method,
        );
      }
    } catch (error) {
      console.error("Error processing payment setup:", error);
      setMessage(
        "An unexpected error occurred while processing your payment method.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Update Payment Method
      </Typography>
      <PaymentElement />

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isProcessing || !stripe || !elements}
          sx={{
            bgcolor: "rgb(2, 129, 192)",
            "&:hover": {
              bgcolor: "rgb(1, 103, 153)",
            }
          }}
        >
          {isProcessing ? "Processing..." : "Save Payment Method"}
        </Button>
      </Box>

      {message && (
        <Typography 
          sx={{ 
            mt: 2, 
            textAlign: "center",
            color: message.includes("successfully") ? "rgb(2, 129, 192)" : "error.main"
          }}
        >
          {message}
        </Typography>
      )}
    </form>
  );
};

const BillingInfo = ({ preferences, onPreferencesUpdate, onPaymentSuccess }) => {
  const user = useSelector(selectUser);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        setLoading(true);
        const headers = {
          "User-Identifier": user.UserId,
        };
        const response = await directClient.post(
          endpoints.postSetupIntent(),
          {},
          { headers },
        );
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error("Error fetching setup intent:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClientSecret();
    return () => setClientSecret(null);
  }, [user.UserId]);

  if (loading) {
    return (
      <Card 
        sx={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "350px",
        }}
      >
        <CircularProgress size={40} sx={{ color: 'rgb(2, 129, 192)' }} />
      </Card>
    );
  }

  if (!clientSecret) {
    return (
      <Card 
        sx={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "350px",
        }}
      >
        <Typography color="error">
          Unable to load payment form. Please try again later.
        </Typography>
      </Card>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      variables: {
        spacingUnit: "4px",
        borderRadius: "4px",
        colorPrimary: "#0281C0",
        colorBackground: "#ffffff",
        colorText: "#30313d",
        colorDanger: "#df1b41",
      },
      rules: {},
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <Card
        sx={{
          height: "100%",
          minHeight: "350px",
          display: "flex",
          flexDirection: "column",
          border: "1px solid lightgray",
        }}
      >
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: 3,
          }}
        >
          <PaymentForm onPaymentSuccess={onPaymentSuccess} />
        </CardContent>
      </Card>
    </Elements>
  );
};

export default BillingInfo;
